import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Key, Copy, Trash2, CheckCircle, AlertCircle, ExternalLink, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

const PLAN_LIMITS = { free: 1, starter: 3, pro: 10, enterprise: Infinity };

/* ── small section card ─────────────────────────────────────────────────── */
function Card({ children, className = '' }) {
  return (
    <div className={`glass-card border border-white/[0.06] ${className}`}>
      {children}
    </div>
  );
}

export default function ApiAccess() {
  const { token, user, API_URL } = useAuth();

  const [keys,      setKeys]      = useState([]);
  const [keyName,   setKeyName]   = useState('');
  const [busy,      setBusy]      = useState(false);
  const [error,     setError]     = useState('');
  const [copied,    setCopied]    = useState('');   // key string that was just copied
  const [newKey,    setNewKey]    = useState('');   // full plaintext key shown once after creation
  const [revokeId,  setRevokeId]  = useState(null); // id pending confirmation

  const plan       = user?.plan || 'free';
  const limit      = PLAN_LIMITS[plan] ?? 1;
  const atLimit    = limit !== Infinity && keys.length >= limit;

  /* ── fetch keys ─────────────────────────────────────────────────────── */
  const fetchKeys = useCallback(async () => {
    if (!token) return;
    try {
      const r    = await fetch(`${API_URL}/api/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (r.ok) setKeys(data.apiKeys || []);
    } catch { /* silent */ }
  }, [token, API_URL]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  /* ── generate ───────────────────────────────────────────────────────── */
  const handleGenerate = async () => {
    const name = keyName.trim();
    if (!name) { setError('Please enter a name for your API key'); return; }
    if (atLimit) { setError(`Your ${plan} plan allows ${limit} key${limit > 1 ? 's' : ''}. Upgrade to add more.`); return; }
    setBusy(true); setError(''); setNewKey('');
    try {
      const r    = await fetch(`${API_URL}/api/api-keys`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to generate key');
      setKeys(prev => [data.apiKeyData, ...prev]);
      setNewKey(data.apiKey);   // show full key once
      setKeyName('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  /* ── copy ───────────────────────────────────────────────────────────── */
  const copyText = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(text);
    setTimeout(() => setCopied(''), 2000);
  };

  /* ── revoke ─────────────────────────────────────────────────────────── */
  const handleRevoke = async (id) => {
    setRevokeId(null);
    try {
      const r = await fetch(`${API_URL}/api/api-keys/${id}`, {
        method:  'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setKeys(prev => prev.filter(k => k._id !== id));
      else {
        const data = await r.json();
        setError(data.error || 'Failed to revoke key');
      }
    } catch { setError('Network error'); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">API Access</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Generate API keys to integrate ZenithCipher into your applications
        </p>
      </div>

      {/* Usage bar */}
      <Card className="p-4 mb-5 flex items-center gap-4">
        <Key size={15} className="text-[#00E5FF] flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-slate-400">
              API Keys — <span className="font-semibold text-white capitalize">{plan}</span> plan
            </span>
            <span className="text-xs text-slate-500 tabular-nums">
              {keys.length} / {limit === Infinity ? '∞' : limit}
            </span>
          </div>
          <div className="h-1 bg-white/[0.07] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${atLimit ? 'bg-red-500' : 'bg-[#00E5FF]'}`}
              style={{ width: limit === Infinity ? '20%' : `${Math.min((keys.length / limit) * 100, 100)}%` }}
            />
          </div>
        </div>
        {atLimit && (
          <Link
            to="/dashboard/billing"
            className="text-xs font-bold text-[#00E5FF] hover:underline flex-shrink-0"
          >
            Upgrade
          </Link>
        )}
      </Card>

      {/* Generate form */}
      <Card className="p-5 mb-5">
        <h2 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
          <Plus size={14} className="text-[#00E5FF]" />
          Generate New Key
        </h2>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl px-4 py-2.5 text-xs mb-4">
            <AlertCircle size={13} className="flex-shrink-0" /> {error}
          </div>
        )}

        <div className="flex gap-3">
          <input
            id="api-key-name"
            type="text"
            placeholder="e.g. My Trading Bot"
            value={keyName}
            onChange={e => { setKeyName(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleGenerate()}
            disabled={busy || atLimit}
            className={[
              'flex-1 bg-white/[0.04] border rounded-xl px-4 py-2.5 text-sm text-slate-200',
              'placeholder-slate-700 outline-none transition-colors',
              'border-white/[0.08] focus:border-[#00E5FF]/40',
              (busy || atLimit) ? 'opacity-50 cursor-not-allowed' : '',
            ].join(' ')}
          />
          <button
            onClick={handleGenerate}
            disabled={busy || atLimit || !keyName.trim()}
            className="flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-xl bg-[#00E5FF] hover:bg-[#00cce5] text-black transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            {busy ? (
              <span className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
            ) : (
              <Key size={13} />
            )}
            {busy ? 'Generating…' : 'Generate'}
          </button>
        </div>

        {/* Show new key once */}
        {newKey && (
          <div className="mt-4 p-4 bg-green-500/8 border border-green-500/20 rounded-xl">
            <p className="text-xs text-green-400 font-semibold mb-2 flex items-center gap-1.5">
              <CheckCircle size={12} /> Key created — copy it now, it won't be shown again
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs text-green-300 bg-black/30 px-3 py-2 rounded-lg overflow-hidden text-ellipsis whitespace-nowrap">
                {newKey}
              </code>
              <button
                onClick={() => copyText(newKey)}
                className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-green-500/15 hover:bg-green-500/25 text-green-400 transition-all flex-shrink-0"
              >
                {copied === newKey ? <CheckCircle size={12} /> : <Copy size={12} />}
                {copied === newKey ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Keys list */}
      <Card className="p-5 mb-6">
        <h2 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
          <Key size={14} className="text-slate-500" />
          Your API Keys
          <span className={`ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            keys.length === 0
              ? 'bg-slate-700 text-slate-400'
              : atLimit
              ? 'bg-red-500/15 text-red-400'
              : 'bg-green-500/10 text-green-400'
          }`}>
            {keys.length} / {limit === Infinity ? '∞' : limit}
          </span>
        </h2>

        {keys.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-white/[0.07] rounded-xl">
            <Key size={24} className="text-slate-700 mx-auto mb-2" />
            <p className="text-slate-500 text-sm mb-1">No API keys yet</p>
            <p className="text-slate-700 text-xs">Generate your first key above to start integrating.</p>
            <button
              onClick={() => document.getElementById('api-key-name')?.focus()}
              className="mt-4 text-xs font-bold text-[#00E5FF] hover:underline"
            >
              Create First Key →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {keys.map(k => (
              <div
                key={k._id}
                className="bg-white/[0.03] border border-white/[0.05] rounded-xl p-4 flex items-center gap-4 hover:border-white/[0.10] transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
                  <Key size={13} className="text-[#00E5FF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{k.name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <code className="font-mono text-[11px] text-slate-600">
                      {k.key?.slice(0, 8)}••••••••{k.key?.slice(-4)}
                    </code>
                    <span className="text-[11px] text-slate-700">
                      Created {new Date(k.createdAt).toLocaleDateString()}
                    </span>
                    {k.usageCount > 0 && (
                      <span className="text-[11px] text-[#00E5FF]/70 tabular-nums">
                        {k.usageCount.toLocaleString()} calls
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => copyText(k.key)}
                    title="Copy key"
                    className="p-2 text-slate-600 hover:text-[#00E5FF] hover:bg-[#00E5FF]/8 rounded-lg transition-all"
                  >
                    {copied === k.key ? <CheckCircle size={14} className="text-green-400" /> : <Copy size={14} />}
                  </button>

                  {/* Revoke with inline confirm */}
                  {revokeId === k._id ? (
                    <div className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/20 rounded-lg px-2 py-1">
                      <span className="text-[11px] text-red-400">Revoke?</span>
                      <button
                        onClick={() => handleRevoke(k._id)}
                        className="text-[11px] font-bold text-red-400 hover:text-red-300 transition-colors"
                      >
                        Yes
                      </button>
                      <span className="text-slate-700 text-[11px]">/</span>
                      <button
                        onClick={() => setRevokeId(null)}
                        className="text-[11px] text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setRevokeId(k._id)}
                      title="Revoke key"
                      className="p-2 text-slate-600 hover:text-red-400 hover:bg-red-500/8 rounded-lg transition-all"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Quick start */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
          Quick Start
          <Link
            to="/docs"
            className="ml-auto text-[11px] text-[#00E5FF] hover:underline flex items-center gap-1"
          >
            Full docs <ExternalLink size={10} />
          </Link>
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-slate-600 mb-2 font-medium uppercase tracking-wider">Request</p>
            <pre className="bg-black/40 border border-white/[0.06] rounded-xl p-4 font-mono text-xs text-slate-400 overflow-x-auto">
{`curl "https://api.zenithcipher.io/api/scan/0x742d35..."
  -H "x-api-key: YOUR_KEY_HERE"`}
            </pre>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-2 font-medium uppercase tracking-wider">Response</p>
            <pre className="bg-black/40 border border-white/[0.06] rounded-xl p-4 font-mono text-xs text-slate-400 overflow-x-auto">
{`{
  "riskScore": 25,
  "riskLevel": "Low",
  "reputationScore": 75,
  "warnings": [],
  "balanceETH": 1.23
}`}
            </pre>
          </div>
        </div>
      </Card>

    </div>
  );
}
